import React, { useState, useRef, useEffect } from "react";
import intlTelInput from "intl-tel-input";
import "intl-tel-input/build/css/intlTelInput.css";
import "intl-tel-input/build/js/utils";

// Reusable Date Input Component with Placeholder
const DateInputWithPlaceholder = ({ name, placeholder, value, onChange, className = "form-control", required = false }) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <input
        ref={inputRef}
        type="date"
        name={name}
        className={className}
        onChange={onChange}
        value={value || ""}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        required={required}
        style={{
          color: value ? '#000' : 'transparent',
        }}
      />
      {!value && !isFocused && (
        <span
          onClick={() => {
            if (inputRef.current) {
              if (typeof inputRef.current.showPicker === 'function') {
                inputRef.current.showPicker();
              } else {
                inputRef.current.focus();
              }
            }
          }}
          style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#6c757d',
            pointerEvents: 'none',
            fontSize: '14px',
            zIndex: 1,
            whiteSpace: 'nowrap'
          }}
        >
          {placeholder}
        </span>
      )}
    </div>
  );
};

function BookingModal({ show, onClose, service }) {
  const [step, setStep] = useState(1);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({});
  const [savedPhoneNumber, setSavedPhoneNumber] = useState(""); // Store phone number separately

  const phoneRef = useRef(null);
  const itiRef = useRef(null);

  const getType = () => {
    const s = service?.toLowerCase() || "";
    if (s.includes("agri")) return "agri";
    if (s.includes("safari")) return "safari";
    if (s.includes("mice")) return "mice";
    if (s.includes("corporate")) return "corporate";
    if (s.includes("team")) return "team";
    return "general";
  };

  const type = getType();

  // Initialize intl-tel-input when modal opens and step is 1
  useEffect(() => {
    if (show && step === 1 && phoneRef.current && !itiRef.current) {
      itiRef.current = intlTelInput(phoneRef.current, {
        initialCountry: "ke",
        preferredCountries: ["ke", "ug", "tz", "us", "gb"],
        nationalMode: false,
        separateDialCode: true,
        utilsScript: "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.19/js/utils.js",
      });

      // Restore previously saved phone number
      if (savedPhoneNumber) {
        itiRef.current.setNumber(savedPhoneNumber);
      }
    }
  }, [show, step, savedPhoneNumber]);

  // Save phone number whenever it changes
  const savePhoneNumber = () => {
    if (itiRef.current) {
      const phoneNumber = itiRef.current.getNumber();
      setSavedPhoneNumber(phoneNumber);
      setFormData(prev => ({
        ...prev,
        phone: phoneNumber
      }));
    }
  };

  // Cleanup when modal closes or step changes away from 1
  useEffect(() => {
    if (!show || step !== 1) {
      if (itiRef.current) {
        // Save the current phone number before destroying
        if (itiRef.current.getNumber()) {
          const phoneNumber = itiRef.current.getNumber();
          setSavedPhoneNumber(phoneNumber);
          setFormData(prev => ({
            ...prev,
            phone: phoneNumber
          }));
        }
        itiRef.current.destroy();
        itiRef.current = null;
      }
    }

    // Re-initialize when coming back to step 1
    if (show && step === 1 && phoneRef.current && !itiRef.current) {
      const timer = setTimeout(() => {
        if (phoneRef.current && !itiRef.current) {
          itiRef.current = intlTelInput(phoneRef.current, {
            initialCountry: "ke",
            preferredCountries: ["ke", "ug", "tz", "us", "gb"],
            nationalMode: false,
            separateDialCode: true,
            utilsScript: "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.19/js/utils.js",
          });

          // Restore saved phone number
          if (savedPhoneNumber) {
            itiRef.current.setNumber(savedPhoneNumber);
          }
        }
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [show, step, savedPhoneNumber]);

  // Reset form when modal closes completely
  useEffect(() => {
    if (!show) {
      setStep(1);
      setSuccess(false);
      setFormData({});
      setSavedPhoneNumber("");
      if (itiRef.current) {
        itiRef.current.destroy();
        itiRef.current = null;
      }
    }
  }, [show]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (type === "select-multiple") {
      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
      setFormData(prev => ({
        ...prev,
        [name]: selectedOptions
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const next = () => {
    // Save phone number before moving to next step
    if (itiRef.current) {
      const phoneNumber = itiRef.current.getNumber();
      setSavedPhoneNumber(phoneNumber);
      setFormData(prev => ({
        ...prev,
        phone: phoneNumber
      }));

      // Validate phone number
      if (!phoneNumber || phoneNumber.length < 8) {
        alert("Please enter a valid phone number");
        return;
      }
    }
    setStep(step + 1);
  };

  const prev = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let phone = formData.phone || savedPhoneNumber;

    if (!phone || phone.length < 8) {
      alert("Please enter a valid phone number");
      setStep(1);
      return;
    }

    const payload = {
      ...formData,
      phone: phone,
      service,
      type,
      created_at: new Date()
    };

    console.log("Booking submission payload:", JSON.stringify(payload, null, 2));

    try {
      const res = await fetch(process.env.REACT_APP_API_URL_EMAIL + "/send-booking", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const responseData = await res.json();
      console.log("Booking submission response:", responseData);

      if (res.status === 200) {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          setStep(1);
          setFormData({});
          setSavedPhoneNumber("");
          if (itiRef.current) {
            itiRef.current.destroy();
            itiRef.current = null;
          }
          onClose();
        }, 2500);
      } else {
        alert("Submission failed. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting booking:", error);
      alert("An error occurred. Please check your connection and try again.");
    }
  };

  if (!show) return null;

  const renderCheckboxGroup = (name, options, label) => (
    <div className="mb-3">
      <label className="form-label fw-semibold">{label}</label>
      {options.map(option => (
        <div className="form-check" key={option}>
          <input
            className="form-check-input"
            type="checkbox"
            name={name}
            value={option}
            checked={formData[name]?.includes(option) || false}
            onChange={(e) => {
              const current = formData[name] || [];
              const value = e.target.value;
              const newValue = current.includes(value)
                ? current.filter(v => v !== value)
                : [...current, value];
              setFormData(prev => ({ ...prev, [name]: newValue }));
            }}
          />
          <label className="form-check-label">{option}</label>
        </div>
      ))}
    </div>
  );

  return (
    <div className="modal fade show d-block" style={{ background: "#00000088", zIndex: 1050 }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content p-4">

          <button
            type="button"
            className="btn-close ms-auto"
            onClick={() => {
              if (itiRef.current) {
                const phoneNumber = itiRef.current.getNumber();
                setSavedPhoneNumber(phoneNumber);
              }
              onClose();
            }}
          ></button>

          {!success ? (
            <>
              <h4 className="icon-color mb-3">{service}</h4>

              <div className="progress mb-4" style={{ height: "6px" }}>
                <div
                  className="progress-bar navyblue-bg"
                  style={{ width: `${(step / 3) * 100}%` }}
                />
              </div>

              <form onSubmit={handleSubmit}>
                <div className="row g-3">

                  {/* STEP 1: CONTACT / BASIC */}
                  {step === 1 && (
                    <>
                      <div className="col-md-6">
                        <input
                          name="name"
                          placeholder="Full Name"
                          className="form-control"
                          onChange={handleChange}
                          value={formData.name || ""}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <input
                          name="email"
                          placeholder="Email Address"
                          type="email"
                          className="form-control"
                          onChange={handleChange}
                          value={formData.email || ""}
                          required
                        />
                      </div>
                      <div className="col-md-12">
                        <input
                          ref={phoneRef}
                          type="tel"
                          className="form-control"
                          placeholder="Phone Number (WhatsApp preferred)"
                          onBlur={() => savePhoneNumber()}
                          onChange={() => savePhoneNumber()}
                        />
                        <small className="text-muted">
                          <i className="fas fa-info-circle me-1"></i>
                          Enter your phone number with country code
                        </small>
                      </div>
                      <div className="col-md-12">
                        <input
                          name="country"
                          placeholder="Country"
                          className="form-control"
                          onChange={handleChange}
                          value={formData.country || ""}
                          required
                        />
                      </div>
                      {(type === "mice" || type === "corporate") && (
                        <div className="col-md-12">
                          <input
                            name="company"
                            placeholder="Company Name"
                            className="form-control"
                            onChange={handleChange}
                            value={formData.company || ""}
                            required
                          />
                        </div>
                      )}
                    </>
                  )}

                  {/* STEP 2: SERVICE-SPECIFIC DETAILS - Same as your original code */}
                  {step === 2 && (
                    <>
                      {/* AGRI */}
                      {type === "agri" && (
                        <>
                          <div className="col-md-12">
                            <DateInputWithPlaceholder
                              name="preferredDate"
                              placeholder="Select preferred date"
                              value={formData.preferredDate}
                              onChange={handleChange}
                            />
                          </div>
                          <div className="col-md-12">
                            <input
                              name="visitors"
                              className="form-control"
                              placeholder="Number of Visitors"
                              onChange={handleChange}
                              value={formData.visitors || ""}
                            />
                          </div>
                          <div className="col-md-12">
                            <select
                              name="groupType"
                              className="form-control"
                              onChange={handleChange}
                              value={formData.groupType || ""}
                            >
                              <option value="">Type of Group</option>
                              <option>School</option>
                              <option>Corporate</option>
                              <option>Family</option>
                              <option>Tour Group</option>
                            </select>
                          </div>
                          <div className="col-md-12">
                            <select
                              name="experienceType"
                              className="form-control"
                              onChange={handleChange}
                              value={formData.experienceType || ""}
                            >
                              <option value="">Experience Type</option>
                              <option>Farm Visit</option>
                              <option>Dairy/Poultry Experience</option>
                              <option>Training / Educational Tour</option>
                              <option>Agri-Investment Tour</option>
                            </select>
                          </div>
                          <div className="col-md-12">
                            <input
                              name="locationPreference"
                              className="form-control"
                              placeholder="Location Preference (if any)"
                              onChange={handleChange}
                              value={formData.locationPreference || ""}
                            />
                          </div>
                          <div className="col-md-6">
                            <select
                              name="transportNeeded"
                              className="form-control"
                              onChange={handleChange}
                              value={formData.transportNeeded || ""}
                            >
                              <option value="">Transport Needed?</option>
                              <option>Yes</option>
                              <option>No</option>
                            </select>
                          </div>
                          <div className="col-md-6">
                            <select
                              name="mealsRequired"
                              className="form-control"
                              onChange={handleChange}
                              value={formData.mealsRequired || ""}
                            >
                              <option value="">Meals Required?</option>
                              <option>Yes</option>
                              <option>No</option>
                            </select>
                          </div>
                        </>
                      )}

                      {/* SAFARI */}
                      {type === "safari" && (
                        <>
                          <div className="col-md-12">
                            <input
                              name="destination"
                              className="form-control"
                              placeholder="Destination"
                              onChange={handleChange}
                              value={formData.destination || ""}
                            />
                          </div>
                          <div className="col-md-6">
                            <DateInputWithPlaceholder
                              name="startDate"
                              placeholder="Start date"
                              value={formData.startDate}
                              onChange={handleChange}
                            />
                          </div>
                          <div className="col-md-6">
                            <DateInputWithPlaceholder
                              name="endDate"
                              placeholder="End date"
                              value={formData.endDate}
                              onChange={handleChange}
                            />
                          </div>
                          <div className="col-md-6">
                            <input
                              name="adults"
                              className="form-control"
                              placeholder="Number of Adults"
                              onChange={handleChange}
                              value={formData.adults || ""}
                            />
                          </div>
                          <div className="col-md-6">
                            <input
                              name="children"
                              className="form-control"
                              placeholder="Number of Children"
                              onChange={handleChange}
                              value={formData.children || ""}
                            />
                          </div>
                          <div className="col-md-6">
                            <select
                              name="accommodation"
                              className="form-control"
                              onChange={handleChange}
                              value={formData.accommodation || ""}
                            >
                              <option value="">Accommodation Type</option>
                              <option>Budget</option>
                              <option>Mid-range</option>
                              <option>Luxury</option>
                            </select>
                          </div>
                          <div className="col-md-6">
                            <select
                              name="transport"
                              className="form-control"
                              onChange={handleChange}
                              value={formData.transport || ""}
                            >
                              <option value="">Transport</option>
                              <option>Tour Van</option>
                              <option>Land Cruiser</option>
                              <option>Flight</option>
                            </select>
                          </div>
                          <div className="col-md-12">
                            <select
                              name="experienceType"
                              className="form-control"
                              onChange={handleChange}
                              value={formData.experienceType || ""}
                            >
                              <option value="">Experience Type</option>
                              <option>Private Safari</option>
                              <option>Group Joining Safari</option>
                              <option>Honeymoon</option>
                              <option>Family Holiday</option>
                              <option>Other</option>
                            </select>
                          </div>
                          {formData.experienceType === "Other" && (
                            <div className="col-md-12">
                              <input
                                name="otherExperience"
                                className="form-control"
                                placeholder="Please specify"
                                onChange={handleChange}
                                value={formData.otherExperience || ""}
                              />
                            </div>
                          )}
                        </>
                      )}

                      {/* MICE */}
                      {type === "mice" && (
                        <>
                          <div className="col-md-12">
                            <select
                              name="eventType"
                              className="form-control"
                              onChange={handleChange}
                              value={formData.eventType || ""}
                            >
                              <option value="">Event Type</option>
                              <option>Conference</option>
                              <option>Incentive Trip</option>
                              <option>Exhibition</option>
                              <option>Corporate Retreat</option>
                            </select>
                          </div>
                          <div className="col-md-12">
                            <input
                              name="destination"
                              className="form-control"
                              placeholder="Destination"
                              onChange={handleChange}
                              value={formData.destination || ""}
                            />
                          </div>
                          <div className="col-md-6">
                            <DateInputWithPlaceholder
                              name="eventDate"
                              placeholder="Event date"
                              value={formData.eventDate}
                              onChange={handleChange}
                            />
                          </div>
                          <div className="col-md-6">
                            <input
                              name="attendees"
                              className="form-control"
                              placeholder="Number of Attendees"
                              onChange={handleChange}
                              value={formData.attendees || ""}
                            />
                          </div>
                          <div className="col-md-12">
                            <label className="form-label">Requirements (select all that apply)</label>
                            {renderCheckboxGroup("requirements", ["Venue Sourcing", "Accommodation", "Transport & Transfers", "Conference Equipment", "Catering"], "")}
                          </div>
                          <div className="col-md-12">
                            <input
                              name="estimatedBudget"
                              className="form-control"
                              placeholder="Estimated Budget"
                              onChange={handleChange}
                              value={formData.estimatedBudget || ""}
                            />
                          </div>
                        </>
                      )}

                      {/* CORPORATE */}
                      {type === "corporate" && (
                        <>
                          <div className="col-md-12">
                            <select
                              name="serviceRequired"
                              className="form-control"
                              onChange={handleChange}
                              value={formData.serviceRequired || ""}
                            >
                              <option value="">Service Required</option>
                              <option>Flight Bookings</option>
                              <option>Hotel Reservations</option>
                              <option>Airport Transfers</option>
                              <option>Full Travel Management</option>
                            </select>
                          </div>
                          <div className="col-md-12">
                            <input
                              name="typicalDestinations"
                              className="form-control"
                              placeholder="Typical Destinations"
                              onChange={handleChange}
                              value={formData.typicalDestinations || ""}
                            />
                          </div>
                          <div className="col-md-6">
                            <select
                              name="travelFrequency"
                              className="form-control"
                              onChange={handleChange}
                              value={formData.travelFrequency || ""}
                            >
                              <option value="">Travel Frequency</option>
                              <option>One-time</option>
                              <option>Regular</option>
                              <option>Frequent</option>
                            </select>
                          </div>
                          <div className="col-md-6">
                            <input
                              name="numberOfTravelers"
                              className="form-control"
                              placeholder="Number of Travelers"
                              onChange={handleChange}
                              value={formData.numberOfTravelers || ""}
                            />
                          </div>
                          <div className="col-md-12">
                            <input
                              name="travelClassPolicy"
                              className="form-control"
                              placeholder="Travel Class Policy"
                              onChange={handleChange}
                              value={formData.travelClassPolicy || ""}
                            />
                          </div>
                          <div className="col-md-12">
                            <input
                              name="hotelCategory"
                              className="form-control"
                              placeholder="Hotel Category Preference"
                              onChange={handleChange}
                              value={formData.hotelCategory || ""}
                            />
                          </div>
                          <div className="col-md-12">
                            <input
                              name="budgetGuidelines"
                              className="form-control"
                              placeholder="Budget Guidelines"
                              onChange={handleChange}
                              value={formData.budgetGuidelines || ""}
                            />
                          </div>
                        </>
                      )}

                      {/* TEAM BUILDING */}
                      {type === "team" && (
                        <>
                          <div className="col-md-12">
                            <DateInputWithPlaceholder
                              name="preferredDate"
                              placeholder="Select preferred date"
                              value={formData.preferredDate}
                              onChange={handleChange}
                            />
                          </div>
                          <div className="col-md-12">
                            <input
                              name="participants"
                              className="form-control"
                              placeholder="Number of Participants"
                              onChange={handleChange}
                              value={formData.participants || ""}
                            />
                          </div>
                          <div className="col-md-12">
                            <select
                              name="locationPreference"
                              className="form-control"
                              onChange={handleChange}
                              value={formData.locationPreference || ""}
                            >
                              <option value="">Location Preference</option>
                              <option>Nairobi & Surrounds</option>
                              <option>Out-of-Town (Naivasha, Nanyuki, etc.)</option>
                              <option>Open to Suggestions</option>
                            </select>
                          </div>
                          <div className="col-md-12">
                            <select
                              name="teamBuildingType"
                              className="form-control"
                              onChange={handleChange}
                              value={formData.teamBuildingType || ""}
                            >
                              <option value="">Team Building Type</option>
                              <option>Outdoor Adventure</option>
                              <option>Indoor Activities</option>
                              <option>High-Energy / Physical</option>
                              <option>Strategy & Problem Solving</option>
                            </select>
                          </div>
                          <div className="col-md-12">
                            <select
                              name="objectives"
                              className="form-control"
                              onChange={handleChange}
                              value={formData.objectives || ""}
                            >
                              <option value="">Objectives</option>
                              <option>Team Bonding</option>
                              <option>Leadership Development</option>
                              <option>Communication Improvement</option>
                              <option>Motivation / Reward</option>
                            </select>
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {/* STEP 3: EXTRAS & ADDITIONAL INFO */}
                  {step === 3 && (
                    <>
                      {type === "safari" && (
                        <>
                          {renderCheckboxGroup("extras", ["Balloon Safari", "Cultural Visits", "Beach Extension"], "Extras")}
                          <textarea
                            name="otherRequests"
                            className="form-control mb-3"
                            placeholder="Other Requests"
                            onChange={handleChange}
                            value={formData.otherRequests || ""}
                          />
                        </>
                      )}

                      {type === "mice" && (
                        <>
                          {renderCheckboxGroup("addons", ["Team Building Activities", "Excursions / Tours"], "Experience Add-ons")}
                          <textarea
                            name="objectiveOfEvent"
                            className="form-control mb-3"
                            placeholder="Objective of Event"
                            onChange={handleChange}
                            value={formData.objectiveOfEvent || ""}
                          />
                        </>
                      )}

                      {type === "corporate" && (
                        <>
                          {renderCheckboxGroup("additionalServices", ["Visa Assistance", "Travel Insurance", "Reporting & Expense Tracking"], "Additional Services")}
                        </>
                      )}

                      {type === "team" && (
                        <>
                          {renderCheckboxGroup("extras", ["Transport Needed", "Meals / Catering", "Accommodation"], "Extras")}
                        </>
                      )}

                      <textarea
                        name="specialRequests"
                        className="form-control"
                        placeholder={type === "agri" ? "Objectives of Visit & Special Requests" : "Special Requests / Additional Information"}
                        rows="3"
                        onChange={handleChange}
                        value={formData.specialRequests || ""}
                      />
                    </>
                  )}

                  {/* Buttons */}
                  <div className="col-12 d-flex justify-content-between mt-3">
                    {step > 1 && (
                      <button type="button" className="btn btn-secondary" onClick={prev}>
                        <i className="fas fa-arrow-left me-2"></i>
                        Back
                      </button>
                    )}
                    {step < 3 ? (
                      <button type="button" className="btn navyblue-bg" onClick={next}>
                        Next
                        <i className="fas fa-arrow-right ms-2"></i>
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn navyblue-bg"
                        onClick={handleSubmit}
                      >
                        <i className="fas fa-paper-plane me-2"></i>
                        Submit Booking
                      </button>
                    )}
                  </div>

                </div>
              </form>
            </>
          ) : (
            <div className="text-center py-5">
              <i className="fa fa-check-circle icon-color fa-4x mb-3"></i>
              <h4 className="icon-color">Booking Submitted!</h4>
              <p className="text-muted">We'll get back to you within 24 hours.</p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default BookingModal;